import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Grid as MuiGrid,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  useMediaQuery,
  useTheme,
  Badge,
  Collapse,
  LinearProgress,
  Divider,
  DialogContentText,
  Tabs,
  Tab,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import SecurityIcon from '@mui/icons-material/Security';
import Autocomplete from '@mui/material/Autocomplete';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EmailIcon from '@mui/icons-material/Email';
import ChatIcon from '@mui/icons-material/Chat';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MessageIcon from '@mui/icons-material/Message';

// Services
import { 
  updateRegistration, 
  createRegistration, 
  deleteRegistration,
  registerVolunteer,
  getJobsForJobManager,
  sendTimeslotInfoEmail,
  updateRegistrationConfirmationStatus,
} from '../services/api';

// Types
import { Timeslot, Volunteer, Registration, JobManagerData, JobManagerJob, JobManagerVolunteer } from '../models/types';
import { SelectChangeEvent } from '@mui/material';

// Fix for MUI Grid component props issue
const Grid = (props: any) => <MuiGrid {...props} />;

// Helper function to format phone number for WhatsApp
const formatPhoneForWhatsApp = (phoneNumber: string): string => {
  // Remove all non-digit characters
  let cleaned = phoneNumber.replace(/\D/g, '');
  
  // If it starts with 0, replace with +43 (Austria)
  if (cleaned.startsWith('0')) {
    cleaned = '43' + cleaned.substring(1);
  }
  // If it doesn't start with a country code, assume Austria (+43)
  else if (!cleaned.startsWith('43') && !cleaned.startsWith('49') && !cleaned.startsWith('41')) {
    cleaned = '43' + cleaned;
  }
  
  return cleaned;
};

// Interface for timeslots with extended information
interface TimeslotWithInfo extends Timeslot {
  registeredVolunteers: number;
  spotsLeft: number;
  spotsPercent: number;
  registrations: Registration[];
  isExpanded?: boolean;
}

// Interface for volunteer selection in new registration
interface VolunteerForSelect extends Volunteer {
  alreadyRegistered?: boolean; 
  hasConflict?: boolean;
}

// Interface for the registration form errors
interface RegistrationFormErrors {
  [key: string]: string;
}

interface NewVolunteerFormRefs {
  firstname: React.RefObject<HTMLInputElement>;
  lastname: React.RefObject<HTMLInputElement>;
  email: React.RefObject<HTMLInputElement>;
  phoneNumber: React.RefObject<HTMLInputElement>;
  commodity: React.RefObject<HTMLSelectElement>;
}

// Helper function to map JobManagerRegistration to Registration
const mapJobManagerRegistrationToRegistration = (reg: any): Registration => ({
  id: reg.id,
  volunteerId: reg.volunteerId,
  timeslotId: reg.timeslotId,
  jobId: reg.jobId,
  registeredAt: reg.registeredAt.toString(),
  isConfirmed: reg.isConfirmed,
  hasAttended: reg.hasAttended,
  actualMinutesWorked: reg.actualMinutesWorked,
  comment: reg.comment,
  volunteerName: reg.volunteerName,
  isOrdner: reg.isOrdner,
  isErsthelfer: reg.isErsthelfer,
  emailConfirmationSent: reg.emailConfirmationSent || false,
  emailConfirmationSentAt: reg.emailConfirmationSentAt,
  whatsAppConfirmationSent: reg.whatsAppConfirmationSent || false,
  whatsAppConfirmationSentAt: reg.whatsAppConfirmationSentAt,
});

const JobManagerPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));  // State
  const [jobManagerData, setJobManagerData] = useState<JobManagerData | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [selectedJob, setSelectedJob] = useState<JobManagerJob | null>(null);
  const [timeslots, setTimeslots] = useState<TimeslotWithInfo[]>([]);
  const [volunteers, setVolunteers] = useState<JobManagerVolunteer[]>([]);
  const [filteredVolunteers, setFilteredVolunteers] = useState<VolunteerForSelect[]>([]);
  const [volunteerSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeslotLoading, setTimeslotLoading] = useState(false);
  const [newRegistrationDialogOpen, setNewRegistrationDialogOpen] = useState(false);
  const [selectedTimeslotId, setSelectedTimeslotId] = useState<string>('');
  const [selectedVolunteerId, setSelectedVolunteerId] = useState<string>('');
  const [updatingRegistrationId, setUpdatingRegistrationId] = useState<string | null>(null);
  const [registrationFormErrors, setRegistrationFormErrors] = useState<RegistrationFormErrors>({});  const [creatingRegistration, setCreatingRegistration] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [lowCoverageOnly, setLowCoverageOnly] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('all');
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [registrationToDelete, setRegistrationToDelete] = useState<Registration | null>(null);
  const [deletingRegistration, setDeletingRegistration] = useState(false);  const [registrationComment, setRegistrationComment] = useState<string>('');  const [newVolunteerConfirmDialogOpen, setNewVolunteerConfirmDialogOpen] = useState(false);
  const [newVolunteerData, setNewVolunteerData] = useState<{id: string, name: string} | null>(null);  const [selectedTimeslotForConfirmation, setSelectedTimeslotForConfirmation] = useState<string | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState<string | null>(null);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  
  // Add state for tracking which specific actions are loading
  const [markingAttendedId, setMarkingAttendedId] = useState<string | null>(null);

  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedTimeslotForEmail, setSelectedTimeslotForEmail] = useState<TimeslotWithInfo | null>(null);
  const [emailSubject, setEmailSubject] = useState<string>('');
  const [emailMessage, setEmailMessage] = useState<string>('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailResult, setEmailResult] = useState<{
    sentEmails: string[];
    skippedEmails: { volunteerId: string; volunteerName: string; reason: string }[];
    totalRecipients: number;
  } | null>(null);

  const formRefs: NewVolunteerFormRefs = {
    firstname: useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>,
    lastname: useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>,
    email: useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>,
    phoneNumber: useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>,
    commodity: useRef<HTMLSelectElement>(null) as React.RefObject<HTMLSelectElement>,
  };
  useEffect(() => {    
    const fetchJobManagerData = async () => {
      try {
        setLoading(true);
        const data = await getJobsForJobManager();
        setJobManagerData(data);
        
        if (data.jobs.length > 0 && !selectedJobId) {
          setSelectedJobId(data.jobs[0].id);
        }
        
        // Set global volunteers for search/filtering
        setVolunteers(data.volunteers);
      } catch (error) {
        console.error('Error fetching job manager data:', error);
        setError('Daten konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      } finally {
        setLoading(false);
      }    };    
    fetchJobManagerData();
  }, [selectedJobId]);
    useEffect(() => {
      const processSelectedJob = () => {
        if (!jobManagerData || !selectedJobId) return;
        
        try {
          setTimeslotLoading(true);
          
          const selectedJobData = jobManagerData.jobs.find(job => job.id === selectedJobId);
          if (!selectedJobData) {
            setError('Ausgewählter Job wurde nicht gefunden.');
            return;
          }
          
          setSelectedJob(selectedJobData);
            // Convert JobManagerTimeslots to TimeslotWithInfo format
          const enhancedTimeslots: TimeslotWithInfo[] = selectedJobData.timeslots.map(timeslot => {            // Map JobManagerRegistrations to Registration format for compatibility
            const mappedRegistrations: Registration[] = timeslot.registrations.map(mapJobManagerRegistrationToRegistration);
            
            return {
              id: timeslot.id,
              jobId: timeslot.jobId,
              startTime: timeslot.startTime.toString(),
              endTime: timeslot.endTime.toString(),
              requiredVolunteers: timeslot.requiredVolunteers,
              description: timeslot.description || '',
              goodieThreshold: timeslot.goodieThreshold,
              registeredVolunteers: timeslot.filledSpots,
              spotsLeft: timeslot.spotsLeft,
              spotsPercent: timeslot.spotsPercent,
              registrations: mappedRegistrations,
              isExpanded: false
            };
          });
          
          const sortedTimeslots = enhancedTimeslots.sort((a, b) => {
            return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
          });
          
          const dates = sortedTimeslots.map(ts => 
            format(new Date(ts.startTime), 'yyyy-MM-dd')
          );
          const uniqueDates = Array.from(new Set(dates));
          setAvailableDates(uniqueDates);
          
          setTimeslots(sortedTimeslots);
        } catch (error) {
          console.error('Error processing selected job:', error);
          setError('Jobdetails konnten nicht verarbeitet werden.');
        } finally {
          setTimeslotLoading(false);
        }
      };
      processSelectedJob();
  }, [selectedJobId, jobManagerData]);
  
  useEffect(() => {
    if (!volunteers.length) {
      setFilteredVolunteers([]);
      return;
    }
      let filtered: VolunteerForSelect[] = volunteers.map(vol => ({
      id: vol.id,
      name: vol.name,
      email: vol.email,
      phoneNumber: vol.phoneNumber,
      commodity: vol.commodity,
      alreadyRegistered: false,
      hasConflict: false
    }));
    
    if (volunteerSearchTerm) {
      const lowercaseSearch = volunteerSearchTerm.toLowerCase();
      filtered = filtered.filter(volunteer => 
        volunteer.name.toLowerCase().includes(lowercaseSearch) ||
        volunteer.email.toLowerCase().includes(lowercaseSearch) ||
        (volunteer.commodity && volunteer.commodity.toLowerCase().includes(lowercaseSearch))
      );
    }
    
    if (selectedTimeslotId) {
      const selectedTimeslot = timeslots.find(ts => ts.id === selectedTimeslotId);
      if (selectedTimeslot) {
        filtered = filtered.map(volunteer => {
          const isAlreadyRegistered = selectedTimeslot.registrations.some(
            reg => reg.volunteerId === volunteer.id
          );
              
          return {
            ...volunteer,
            alreadyRegistered: isAlreadyRegistered,
          };
        });
      }
    }
    
    setFilteredVolunteers(filtered);
  }, [volunteers, volunteerSearchTerm, selectedTimeslotId, timeslots]);
  
  const filteredTimeslots = timeslots.filter(timeslot => {
    if (lowCoverageOnly && timeslot.spotsPercent >= 75) {
      return false;
    }
    
    if (selectedDate !== 'all') {
      const timeslotDate = format(new Date(timeslot.startTime), 'yyyy-MM-dd');
      if (timeslotDate !== selectedDate) {
        return false;
      }
    }
    
    return true;
  });
  const handleJobChange = (event: SelectChangeEvent) => {
    setSelectedJobId(event.target.value);
    setSelectedDate('all'); // Reset date filter when job changes
  };
  
  const handleDateChange = (event: SelectChangeEvent) => {
    setSelectedDate(event.target.value);
  };
  
  const toggleTimeslotExpansion = (timeslotId: string) => {
    setTimeslots(prevTimeslots => 
      prevTimeslots.map(ts => 
        ts.id === timeslotId ? { ...ts, isExpanded: !ts.isExpanded } : ts
      )
    );
  };
  
  // const handleVolunteerSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setVolunteerSearchTerm(event.target.value);
  // };
  
  const handleOpenNewRegistrationDialog = (timeslotId: string) => {
    setSelectedTimeslotId(timeslotId);
    setSelectedVolunteerId('');
    setRegistrationFormErrors({});
    setNewRegistrationDialogOpen(true);
  };    const handleCloseNewRegistrationDialog = () => {
    setNewRegistrationDialogOpen(false);
    setSelectedTimeslotId('');
    setSelectedVolunteerId('');
    setRegistrationFormErrors({});    
    setSelectedTab(0); // Reset to first tab
    setRegistrationComment(''); // Reset comment
  };
  
  // const handleVolunteerSelect = (event: SelectChangeEvent) => {
  //   setSelectedVolunteerId(event.target.value);
  //   if (registrationFormErrors.volunteer) {
  //     setRegistrationFormErrors({ ...registrationFormErrors, volunteer: '' });
  //   }
  // };
  
  const validateRegistrationForm = () => {
    const errors: { [key: string]: string } = {};
    if (!selectedVolunteerId) {
      errors.volunteer = 'Bitte wählen Sie einen Helfer aus';
    }
    
    const selectedVolunteer = filteredVolunteers.find(v => v.id === selectedVolunteerId);
    if (selectedVolunteer?.alreadyRegistered) {
      errors.volunteer = 'Dieser Helfer ist bereits für dieses Zeitfenster angemeldet';
    }
    
    setRegistrationFormErrors(errors);
    return Object.keys(errors).length === 0;
  };    const handleCreateRegistration = async () => {
    if (!selectedJobId || !selectedTimeslotId) return;
    
    try {
      // Validate before setting loading state
      if (selectedTab === 1 && !validateNewVolunteerForm()) {
        return;
      }
      if (selectedTab === 0 && !validateRegistrationForm()) {
        return;
      }

      setCreatingRegistration(true);
      setRegistrationFormErrors({});
      
      let volunteerId = selectedVolunteerId;
      
      if (selectedTab === 1) {
        // Create new volunteer first
        const response = await registerVolunteer({
          name: `${formRefs.firstname.current?.value.trim() || ''} ${formRefs.lastname.current?.value.trim() || ''}`.trim(),
          email: formRefs.email.current?.value || '',
          phoneNumber: formRefs.phoneNumber.current?.value || undefined,
          commodity: formRefs.commodity.current?.value || undefined,
        });
        volunteerId = response.volunteer.id;
          // Close the registration dialog and show confirmation dialog for new volunteer
        setNewRegistrationDialogOpen(false);
        setNewVolunteerData({ id: response.volunteer.id, name: response.volunteer.name });
        setSelectedTimeslotForConfirmation(selectedTimeslotId);
        setNewVolunteerConfirmDialogOpen(true);
        setCreatingRegistration(false);
        return;
      }
        await createRegistration({
        volunteerId,
        jobId: selectedJobId,
        timeslotId: selectedTimeslotId,
        comment: registrationComment.trim() || undefined
      });
        // Reset form
      if (formRefs.firstname.current) formRefs.firstname.current.value = '';
      if (formRefs.lastname.current) formRefs.lastname.current.value = '';
      if (formRefs.email.current) formRefs.email.current.value = '';
      if (formRefs.phoneNumber.current) formRefs.phoneNumber.current.value = '';
      if (formRefs.commodity.current) formRefs.commodity.current.value = '';
      
      // Refresh data using optimized endpoint
      const updatedJobManagerData = await getJobsForJobManager();
      setJobManagerData(updatedJobManagerData);
        // Update timeslots for selected job
      if (selectedJob) {
        const updatedSelectedJob = updatedJobManagerData.jobs.find(job => job.id === selectedJob.id);
        if (updatedSelectedJob) {
          const enhancedTimeslots: TimeslotWithInfo[] = updatedSelectedJob.timeslots.map(timeslot => {
            const timeslotRegistrations = timeslot.registrations.map(reg => ({
              id: reg.id,
              volunteerId: reg.volunteerId,
              timeslotId: reg.timeslotId,
              jobId: reg.jobId,
              registeredAt: reg.registeredAt,
              isConfirmed: reg.isConfirmed,
              hasAttended: reg.hasAttended,
              actualMinutesWorked: reg.actualMinutesWorked,
              comment: reg.comment,
              volunteerName: reg.volunteerName,
              isOrdner: reg.isOrdner,
              isErsthelfer: reg.isErsthelfer,
              emailConfirmationSent: reg.emailConfirmationSent || false,
              emailConfirmationSentAt: reg.emailConfirmationSentAt,
              whatsAppConfirmationSent: reg.whatsAppConfirmationSent || false,
              whatsAppConfirmationSentAt: reg.whatsAppConfirmationSentAt,
            }));
            
            const registered = timeslotRegistrations.length;
            const spotsLeft = Math.max(0, timeslot.requiredVolunteers - registered);
            const spotsPercent = (registered / timeslot.requiredVolunteers) * 100;
            
            const currentTimeslot = timeslots.find(ts => ts.id === timeslot.id);
            
            return {
              id: timeslot.id,
              jobId: timeslot.jobId,
              startTime: timeslot.startTime,
              endTime: timeslot.endTime,
              requiredVolunteers: timeslot.requiredVolunteers,
              location: updatedSelectedJob.location,
              description: timeslot.description,
              goodieThreshold: timeslot.goodieThreshold,
              registeredVolunteers: registered,
              spotsLeft,
              spotsPercent,
              registrations: timeslotRegistrations,
              isExpanded: currentTimeslot?.isExpanded || false,
            };
          });
          
          setTimeslots(enhancedTimeslots);
        }
      }
      
      // Update volunteers list if new volunteer was created
      if (selectedTab === 1) {
        setVolunteers(updatedJobManagerData.volunteers);
      }
      
      handleCloseNewRegistrationDialog();
    } catch (error: any) {
      console.error('Error creating registration:', error);
      handleApiError(error);
    } finally {
      setCreatingRegistration(false);
    }
  };

  const handleApiError = (error: any) => {
    if (error.response?.data?.error === 'Email already registered') {
      setRegistrationFormErrors({
        email: 'Diese E-Mail-Adresse ist bereits registriert. Bitte wählen Sie einen existierenden Helfer aus.',
        submit: 'Registrierung fehlgeschlagen: E-Mail bereits registriert'
      });
    } else if (error.response?.data?.message?.includes('already registered')) {
      setRegistrationFormErrors({
        submit: 'Dieser Helfer ist bereits für dieses Zeitfenster angemeldet.'
      });
    } else {
      setRegistrationFormErrors({
        submit: 'Registrierung konnte nicht erstellt werden. Bitte versuchen Sie es erneut.'
      });
    }
  };
  const handleConfirmNewVolunteerRegistration = async () => {
    if (!newVolunteerData || !selectedTimeslotForConfirmation || !selectedJobId) return;
    
    try {
      setCreatingRegistration(true);
      
      await createRegistration({
        volunteerId: newVolunteerData.id,
        jobId: selectedJobId,
        timeslotId: selectedTimeslotForConfirmation,
        comment: registrationComment.trim() || undefined
      });
      
      // Refresh data using optimized endpoint
      const updatedJobManagerData = await getJobsForJobManager();
      setJobManagerData(updatedJobManagerData);
      
      // Update timeslots for selected job
      if (selectedJob) {
        const updatedSelectedJob = updatedJobManagerData.jobs.find(job => job.id === selectedJob.id);
        if (updatedSelectedJob) {
          const enhancedTimeslots: TimeslotWithInfo[] = updatedSelectedJob.timeslots.map(timeslot => {
            const timeslotRegistrations = timeslot.registrations.map(reg => ({
              id: reg.id,
              volunteerId: reg.volunteerId,
              timeslotId: reg.timeslotId,
              jobId: reg.jobId,
              registeredAt: reg.registeredAt,
              isConfirmed: reg.isConfirmed,
              hasAttended: reg.hasAttended,
              actualMinutesWorked: reg.actualMinutesWorked,
              comment: reg.comment,
              volunteerName: reg.volunteerName,
              isOrdner: reg.isOrdner,
              isErsthelfer: reg.isErsthelfer,
              emailConfirmationSent: reg.emailConfirmationSent || false,
              emailConfirmationSentAt: reg.emailConfirmationSentAt,
              whatsAppConfirmationSent: reg.whatsAppConfirmationSent || false,
              whatsAppConfirmationSentAt: reg.whatsAppConfirmationSentAt,
            }));
            
            const registered = timeslotRegistrations.length;
            const spotsLeft = Math.max(0, timeslot.requiredVolunteers - registered);
            const spotsPercent = (registered / timeslot.requiredVolunteers) * 100;
            
            const currentTimeslot = timeslots.find(ts => ts.id === timeslot.id);
            
            return {
              id: timeslot.id,
              jobId: timeslot.jobId,
              startTime: timeslot.startTime,
              endTime: timeslot.endTime,
              requiredVolunteers: timeslot.requiredVolunteers,
              location: updatedSelectedJob.location,
              description: timeslot.description,
              goodieThreshold: timeslot.goodieThreshold,
              registeredVolunteers: registered,
              spotsLeft,
              spotsPercent,
              registrations: timeslotRegistrations,
              isExpanded: currentTimeslot?.isExpanded || false,
            };
          });
          
          setTimeslots(enhancedTimeslots);
        }
      }
      
      // Update volunteers list
      setVolunteers(updatedJobManagerData.volunteers);
        // Close confirmation dialog and reset state
      setNewVolunteerConfirmDialogOpen(false);
      setNewVolunteerData(null);
      setSelectedTimeslotForConfirmation(null);
      setRegistrationComment('');
      
      setRegistrationSuccess(`${newVolunteerData.name} wurde erfolgreich angemeldet!`);
    } catch (error: any) {
      console.error('Error creating registration for new volunteer:', error);
      setRegistrationError('Fehler beim Anmelden des neuen Freiwilligen');
    } finally {
      setCreatingRegistration(false);
    }
  };
  
  const handleUpdateRegistrationStatus = async (
    registrationId: string, 
    data: { isConfirmed: boolean; hasAttended: boolean }
  ) => {
    try {
      setUpdatingRegistrationId(registrationId);
      
      // Set specific loading states based on the action
      if (data.hasAttended) {
        setMarkingAttendedId(registrationId);
      }
      
      await updateRegistration(registrationId, data);
      
      // Refresh data using optimized endpoint
      const updatedJobManagerData = await getJobsForJobManager();
      setJobManagerData(updatedJobManagerData);
      
      // Update timeslots for selected job
      if (selectedJob) {
        const updatedSelectedJob = updatedJobManagerData.jobs.find(job => job.id === selectedJob.id);
        if (updatedSelectedJob) {
          const enhancedTimeslots: TimeslotWithInfo[] = updatedSelectedJob.timeslots.map(timeslot => {
            const timeslotRegistrations = timeslot.registrations.map(reg => ({
              id: reg.id,
              volunteerId: reg.volunteerId,
              timeslotId: reg.timeslotId,
              jobId: reg.jobId,
              registeredAt: reg.registeredAt,
              isConfirmed: reg.isConfirmed,
              hasAttended: reg.hasAttended,
              actualMinutesWorked: reg.actualMinutesWorked,
              comment: reg.comment,
              volunteerName: reg.volunteerName,
              isOrdner: reg.isOrdner,
              isErsthelfer: reg.isErsthelfer,
              emailConfirmationSent: reg.emailConfirmationSent || false,
              emailConfirmationSentAt: reg.emailConfirmationSentAt,
              whatsAppConfirmationSent: reg.whatsAppConfirmationSent || false,
              whatsAppConfirmationSentAt: reg.whatsAppConfirmationSentAt,
            }));
            
            const registered = timeslotRegistrations.length;
            const spotsLeft = Math.max(0, timeslot.requiredVolunteers - registered);
            const spotsPercent = (registered / timeslot.requiredVolunteers) * 100;
            
            const currentTimeslot = timeslots.find(ts => ts.id === timeslot.id);
            
            return {
              id: timeslot.id,
              jobId: timeslot.jobId,
              startTime: timeslot.startTime,
              endTime: timeslot.endTime,
              requiredVolunteers: timeslot.requiredVolunteers,              location: updatedSelectedJob.location,
              description: timeslot.description,
              goodieThreshold: timeslot.goodieThreshold,
              registeredVolunteers: registered,
              spotsLeft,
              spotsPercent,
              registrations: timeslotRegistrations,
              isExpanded: currentTimeslot?.isExpanded || false,
            };
          });
          
          setTimeslots(enhancedTimeslots);
        }
      }
      
    } catch (error) {
      console.error('Error updating registration status:', error);
      setError('Status konnte nicht aktualisiert werden.');
    } finally {
      setUpdatingRegistrationId(null);
      setMarkingAttendedId(null);
    }
  };
  
  const handleOpenDeleteDialog = (registration: Registration) => {
    setRegistrationToDelete(registration);
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRegistrationToDelete(null);
  };
  const handleDeleteRegistration = async () => {
    if (!registrationToDelete) return;
    
    try {
      setDeletingRegistration(true);
      
      await deleteRegistration(registrationToDelete.id);
      
      // Refresh data using optimized endpoint
      const updatedJobManagerData = await getJobsForJobManager();
      setJobManagerData(updatedJobManagerData);
      
      // Update timeslots for selected job
      if (selectedJob) {
        const updatedSelectedJob = updatedJobManagerData.jobs.find(job => job.id === selectedJob.id);
        if (updatedSelectedJob) {
          const enhancedTimeslots: TimeslotWithInfo[] = updatedSelectedJob.timeslots.map(timeslot => {
            const timeslotRegistrations = timeslot.registrations.map(reg => ({
              id: reg.id,
              volunteerId: reg.volunteerId,
              timeslotId: reg.timeslotId,
              jobId: reg.jobId,
              registeredAt: reg.registeredAt,
              isConfirmed: reg.isConfirmed,
              hasAttended: reg.hasAttended,
              actualMinutesWorked: reg.actualMinutesWorked,
              comment: reg.comment,
              volunteerName: reg.volunteerName,
              isOrdner: reg.isOrdner,
              isErsthelfer: reg.isErsthelfer,
            }));
            
            const registered = timeslotRegistrations.length;
            const spotsLeft = Math.max(0, timeslot.requiredVolunteers - registered);
            const spotsPercent = (registered / timeslot.requiredVolunteers) * 100;
            
            const currentTimeslot = timeslots.find(ts => ts.id === timeslot.id);
            
            return {
              id: timeslot.id,
              jobId: timeslot.jobId,              startTime: timeslot.startTime,
              endTime: timeslot.endTime,
              requiredVolunteers: timeslot.requiredVolunteers,
              location: updatedSelectedJob.location,
              description: timeslot.description,
              goodieThreshold: timeslot.goodieThreshold,
              registeredVolunteers: registered,
              spotsLeft,
              spotsPercent,
              registrations: timeslotRegistrations,
              isExpanded: currentTimeslot?.isExpanded || false,
            };
          });
          
          setTimeslots(enhancedTimeslots);
        }
      }
      
      handleCloseDeleteDialog();
      
    } catch (error) {
      console.error('Error deleting registration:', error);
      setError('Anmeldung konnte nicht gelöscht werden.');
    } finally {
      setDeletingRegistration(false);
    }
  };
  
  // Handler for updating special roles (Ordner/Ersthelfer)
  const handleUpdateSpecialRole = async (registrationId: string, role: 'ordner' | 'ersthelfer', newValue: boolean) => {
    try {
      setUpdatingRegistrationId(registrationId);
      
      // Find the current registration
      const currentRegistration = timeslots
        .flatMap(ts => ts.registrations)
        .find(reg => reg.id === registrationId);
      
      if (!currentRegistration) {
        setError('Anmeldung nicht gefunden.');
        return;
      }
      
      // Prepare the update data
      const updateData = {
        isConfirmed: true, // Always set to confirmed
        hasAttended: currentRegistration.hasAttended,
        actualMinutesWorked: currentRegistration.actualMinutesWorked,
        comment: currentRegistration.comment,
        isOrdner: role === 'ordner' ? newValue : currentRegistration.isOrdner,
        isErsthelfer: role === 'ersthelfer' ? newValue : currentRegistration.isErsthelfer,
      };
      
      await updateRegistration(registrationId, updateData);
      
      // Refresh data using optimized endpoint
      const updatedJobManagerData = await getJobsForJobManager();
      setJobManagerData(updatedJobManagerData);
      
      // Update timeslots for selected job
      if (selectedJob) {
        const updatedSelectedJob = updatedJobManagerData.jobs.find(job => job.id === selectedJob.id);
        if (updatedSelectedJob) {
          const enhancedTimeslots: TimeslotWithInfo[] = updatedSelectedJob.timeslots.map(timeslot => {
            const timeslotRegistrations = timeslot.registrations.map(reg => ({
              id: reg.id,
              volunteerId: reg.volunteerId,
              timeslotId: reg.timeslotId,
              jobId: reg.jobId,
              registeredAt: reg.registeredAt,
              isConfirmed: reg.isConfirmed,
              hasAttended: reg.hasAttended,
              actualMinutesWorked: reg.actualMinutesWorked,
              comment: reg.comment,
              volunteerName: reg.volunteerName,
              isOrdner: reg.isOrdner,
              isErsthelfer: reg.isErsthelfer,
            }));
            
            const registered = timeslotRegistrations.length;
            const spotsLeft = Math.max(0, timeslot.requiredVolunteers - registered);
            const spotsPercent = (registered / timeslot.requiredVolunteers) * 100;
            
            const currentTimeslot = timeslots.find(ts => ts.id === timeslot.id);
            
            return {
              id: timeslot.id,
              jobId: timeslot.jobId,
              startTime: timeslot.startTime,
              endTime: timeslot.endTime,
              requiredVolunteers: timeslot.requiredVolunteers,
              location: updatedSelectedJob.location,
              description: timeslot.description,
              goodieThreshold: timeslot.goodieThreshold,
              registeredVolunteers: registered,
              spotsLeft,
              spotsPercent,
              registrations: timeslotRegistrations,
              isExpanded: currentTimeslot?.isExpanded || false,
            };
          });
          
          setTimeslots(enhancedTimeslots);
        }
      }
      
    } catch (error) {
      console.error(`Error updating ${role} status:`, error);
      setError(`${role === 'ordner' ? 'Ordner' : 'Ersthelfer'}-Status konnte nicht aktualisiert werden.`);
    } finally {
      setUpdatingRegistrationId(null);
    }
  };

  // Handler for navigating to volunteer profile
  const handleViewVolunteerProfile = (volunteerId: string) => {
    if (volunteerId) {
      navigate(`/admin/volunteers/${volunteerId}`);
    }
  };
   const validateNewVolunteerForm = () => {
    const errors: { [key: string]: string } = {};
    const firstname = formRefs.firstname.current?.value.trim() || '';
    const lastname = formRefs.lastname.current?.value.trim() || '';
    const email = formRefs.email.current?.value.trim() || '';
    const phoneNumber = formRefs.phoneNumber.current?.value.trim() || '';

    if (!firstname) errors.firstname = 'Vorname ist erforderlich';
    if (!lastname) errors.lastname = 'Nachname ist erforderlich';
    
    // Either email or phone must be provided
    if (!email && !phoneNumber) {
      errors.contact = 'Entweder E-Mail oder Telefonnummer ist erforderlich';
    } 
    
    // If email is provided, validate it
    if (email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = 'Ungültige E-Mail-Adresse';
    }

    setRegistrationFormErrors(prevErrors => ({
      ...prevErrors,
      ...errors
    }));
    return Object.keys(errors).length === 0;
  };

  // Email handlers
  const handleOpenEmailDialog = (timeslot: TimeslotWithInfo) => {
    setSelectedTimeslotForEmail(timeslot);
    setEmailSubject(`Information zum Dienst: ${selectedJob?.name} - ${timeslot.description || format(new Date(timeslot.startTime), 'dd.MM.yyyy HH:mm')}`);
    setEmailMessage('');
    setEmailResult(null);
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setSelectedTimeslotForEmail(null);
    setEmailSubject('');
    setEmailMessage('');
    setEmailResult(null);
  };

  const handleSendEmail = async () => {
    if (!selectedTimeslotForEmail || !selectedJob || !emailSubject.trim() || !emailMessage.trim()) {
      return;
    }

    try {
      setSendingEmail(true);
      const result = await sendTimeslotInfoEmail({
        timeslotId: selectedTimeslotForEmail.id,
        jobId: selectedJob.id,
        subject: emailSubject.trim(),
        message: emailMessage.trim(),
      });
      
      setEmailResult(result);
    } catch (error) {
      console.error('Error sending email:', error);
      setError('Fehler beim Versenden der E-Mails. Bitte versuchen Sie es erneut.');
    } finally {
      setSendingEmail(false);
    }
  };


  // WhatsApp confirmation handler
  const handleSendWhatsAppConfirmation = async (registration: Registration) => {
    const volunteer = volunteers.find(v => v.id === registration.volunteerId);
    const phoneNumber = volunteer?.phoneNumber;
    
    if (!phoneNumber || !selectedJob) {
      return;
    }

    // Find the timeslot for this registration
    const timeslot = timeslots.find(ts => 
      ts.registrations.some(reg => reg.id === registration.id)
    );
    
    if (!timeslot) {
      alert('Zeitfenster für diese Anmeldung nicht gefunden.');
      return;
    }

    try {
      // Generate the same confirmation message as in the email
      const timeslotStart = new Date(timeslot.startTime);
      const timeslotEnd = new Date(timeslot.endTime);
      
      const confirmationMessage = `
Hallo ${registration.volunteerName},

Wir freuen uns sehr, deine Anmeldung hiermit zu bestätigen! Dein Einsatz wurde von unserem Team geprüft und freigegeben.

*** Deine bestätigten Einsatzdaten: ***
- Tätigkeit: ${selectedJob.name}
- Datum: ${timeslotStart.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
- Uhrzeit: ${timeslotStart.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} - ${timeslotEnd.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
- Bitte finde dich 15 Minuten vor Beginn am Treffpunkt ein.

*** Abschlussessen - Du bist eingeladen! ***
Als besonderes Dankeschön für deinen wertvollen Beitrag laden wir alle freiwilligen Helferinnen und Helfer herzlich zu unserem Abschlussessen ein:
- Datum: Freitag, 25. September 2026
- Uhrzeit: 19:00 Uhr
- Ort: Heurigen Koch

Ohne deine Unterstützung wäre die Durchführung unserer Veranstaltung nicht möglich. Herzlichen Dank für deine Bereitschaft!

Bei Fragen kontaktiere uns:
- Matthias Kamleitner: 0676 6266084
- Katharina Aigner: 0664 3953799

Wir freuen uns auf eine erfolgreiche Zusammenarbeit!

Mit herzlichen Grüßen,
Dein Strudengauer Messe Team`;

      const formattedPhone = formatPhoneForWhatsApp(phoneNumber);
      const encodedMessage = encodeURIComponent(confirmationMessage);
      const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodedMessage}`;
      
      // Update confirmation status in database
      await updateRegistrationConfirmationStatus(registration.id, {
        whatsAppConfirmationSent: true
      });

      // Refresh data to get updated confirmation status
      const updatedJobManagerData = await getJobsForJobManager();
      setJobManagerData(updatedJobManagerData);
      
      // Open WhatsApp
      window.open(whatsappUrl, '_blank');
    } catch (error) {
      console.error('Error updating WhatsApp confirmation status:', error);
      alert('Fehler beim Aktualisieren des WhatsApp-Status.');
    }
  };

  const renderCoverageIndicator = (timeslot: TimeslotWithInfo) => {
    let color = 'success';
    let label = 'Ausreichend besetzt';
    
    if (timeslot.spotsPercent < 50) {
      color = 'error';
      label = 'Kritisch unterbesetzt';
    } else if (timeslot.spotsPercent < 75) {
      color = 'warning';
      label = 'Unterbesetzt';
    }
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', mt: 1, width: '100%' }}>
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <LinearProgress 
            variant="determinate" 
            value={Math.min(100, timeslot.spotsPercent)} 
            color={color as any}
            sx={{ 
              height: 8, 
              borderRadius: 4, 
              width: '100%',
              backgroundColor: theme.palette.grey[200] 
            }}
          />
          <Typography variant="caption" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
            {`${timeslot.registeredVolunteers}/${timeslot.requiredVolunteers}`}
          </Typography>
        </Box>
        <Typography variant="caption" color={color as any} sx={{ fontWeight: 500 }}>
          {label} {timeslot.spotsLeft > 0 && `(${timeslot.spotsLeft} Plätze frei)`}
        </Typography>
      </Box>
    );
  }; 

  const renderRegistrationItem = (registration: Registration) => (
    <ListItem
      key={registration.id}
      dense
      divider
      sx={{
        backgroundColor: registration.hasAttended 
          ? theme.palette.success.light + '20'
          : 'inherit'
      }}
    >      <ListItemText
        primary={registration.volunteerName}
        secondary={
          <>
            <Box sx={{ mb: 1 }}>
              {registration.hasAttended && 
                <Chip 
                  label="Anwesend" 
                  color="success" 
                  size="small" 
                  sx={{ mr: 1, height: 20 }} 
                  icon={<HowToRegIcon fontSize="small" />}
                />
              }
              {!registration.hasAttended && 
                <Chip 
                  label="Bestätigt" 
                  color="primary" 
                  size="small" 
                  sx={{ mr: 1, height: 20 }} 
                  icon={<CheckCircleIcon fontSize="small" />}
                />
              }
            </Box>
            {registration.comment && (
              <Box sx={{ mt: 5 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block',
                    fontWeight: 'medium',
                    color: 'text.primary',
                    mb: 0.5
                  }}
                >
                  Kommentar:
                </Typography>
                <TextField
                  value={registration.comment}
                  variant="outlined"
                  size="small"
                  multiline
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    width: '100%',
                    maxWidth: 300,
                    '& .MuiInputBase-input': {
                      fontSize: '0.75rem',
                      fontStyle: 'italic',
                      color: 'text.secondary',
                      padding: '6px 8px'
                    },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'action.hover',
                      '& fieldset': {
                        borderColor: 'divider',
                      },
                    }
                  }}
                />
              </Box>
            )}
          </>
        }
        primaryTypographyProps={{ variant: 'body2' }}
        secondaryTypographyProps={{ component: 'div' }}
      />
      <ListItemSecondaryAction sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
        <Tooltip title="Profil anzeigen">
          <IconButton
            size="small"
            color="info"
            onClick={() => handleViewVolunteerProfile(registration.volunteerId)}
            sx={{ border: '1px solid', borderColor: 'info.main', p: '3px' }}
          >
            <AccountCircleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        {/* Ordner role toggle */}
        <Tooltip title={registration.isOrdner ? "Ordner entfernen" : "Als Ordner markieren"}>
          <IconButton
            size="small"
            color={registration.isOrdner ? "secondary" : "default"}
            disabled={updatingRegistrationId === registration.id}
            onClick={() => handleUpdateSpecialRole(registration.id, 'ordner', !registration.isOrdner)}
            sx={{ 
              border: '1px solid', 
              borderColor: registration.isOrdner ? 'secondary.main' : 'grey.400', 
              p: '3px',
              //backgroundColor: registration.isOrdner ? 'secondary.light' : 'transparent'
            }}
          >
            <SecurityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        {/* Ersthelfer role toggle */}
        <Tooltip title={registration.isErsthelfer ? "Ersthelfer entfernen" : "Als Ersthelfer markieren"}>
          <IconButton
            size="small"
            color={registration.isErsthelfer ? "secondary" : "default"}
            disabled={updatingRegistrationId === registration.id}
            onClick={() => handleUpdateSpecialRole(registration.id, 'ersthelfer', !registration.isErsthelfer)}
            sx={{ 
              border: '1px solid', 
              borderColor: registration.isErsthelfer ? 'secondary.main' : 'grey.400', 
              p: '3px',
            //  backgroundColor: registration.isErsthelfer ? 'secondary.light' : 'transparent'
            }}
          >
            <LocalHospitalIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        {/* WhatsApp Confirmation */}
        {(() => {
          const volunteer = volunteers.find(v => v.id === registration.volunteerId);
          const phoneNumber = volunteer?.phoneNumber;
          const whatsappSent = registration.whatsAppConfirmationSent || false;
          const emailSent = registration.emailConfirmationSent || false;
          
          // Show WhatsApp button regardless of email or phone availability
          // But disable if no phone number
          const anySent = whatsappSent || emailSent;
          
          // Create detailed tooltip text
          const getTooltipText = () => {
            if (!phoneNumber) return "Keine Telefonnummer verfügbar";
            
            if (whatsappSent && emailSent) {
              return "Bestätigung erneut senden (bereits per WhatsApp und E-Mail gesendet)";
            } else if (whatsappSent) {
              return "Bestätigung erneut senden (bereits per WhatsApp gesendet)";
            } else if (emailSent) {
              return "Bestätigung erneut senden (bereits per E-Mail gesendet)";
            } else {
              return "Bestätigung per WhatsApp senden";
            }
          };
          
          return (
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <Tooltip title={getTooltipText()}>
                <IconButton
                  size="small"
                  color={anySent ? "warning" : "primary"}
                  disabled={!phoneNumber}
                  onClick={() => handleSendWhatsAppConfirmation(registration)}
                  sx={{ 
                    border: '2px solid', 
                    borderColor: anySent ? 'warning.main' : 'primary.main', 
                    backgroundColor: anySent ? 'warning.light' : 'transparent',
                    p: '3px',
                    opacity: !phoneNumber ? 0.3 : 1,
                    '&:hover': {
                      backgroundColor: anySent ? 'warning.main' : 'primary.light',
                      opacity: !phoneNumber ? 0.3 : 0.8
                    },
                    '&:disabled': {
                      backgroundColor: 'grey.100',
                      borderColor: 'grey.300'
                    },
                    // Add a subtle animation for already sent status
                    ...(anySent && {
                      '@keyframes pulseWarning': {
                        '0%': { boxShadow: '0 0 0 0 rgba(255, 152, 0, 0.4)' },
                        '70%': { boxShadow: '0 0 0 8px rgba(255, 152, 0, 0)' },
                        '100%': { boxShadow: '0 0 0 0 rgba(255, 152, 0, 0)' }
                      },
                      animation: 'pulseWarning 2s infinite'
                    })
                  }}
                >
                  <WhatsAppIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              {/* Confirmation status indicator */}
              {anySent && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    width: 16,
                    height: 16,
                    backgroundColor: 'success.main',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid white',
                    boxShadow: 1
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 12, color: 'white' }} />
                </Box>
              )}
            </Box>
          );
        })()}

        {/* WhatsApp General Contact */}
        {(() => {
          const volunteer = volunteers.find(v => v.id === registration.volunteerId);
          const phoneNumber = volunteer?.phoneNumber;
          if (phoneNumber) {
            return (
              <Tooltip title="WhatsApp öffnen (allgemein)">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => {
                    const formattedPhone = formatPhoneForWhatsApp(phoneNumber);
                    const message = encodeURIComponent(`Hallo ${registration.volunteerName}, Nachricht vom Strudengauer Messe Team.`);
                    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${message}`;
                    window.open(whatsappUrl, '_blank');
                  }}
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'success.main', 
                    p: '3px',
                    backgroundColor: 'rgba(46, 125, 50, 0.1)' // Light green background to differentiate
                  }}
                >
                  <MessageIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            );
          }
          return null;
        })()}
        
        <Tooltip title="Anmeldung löschen">
          <IconButton
            size="small"
            color="error"
            disabled={deletingRegistration || updatingRegistrationId === registration.id}
            onClick={() => handleOpenDeleteDialog(registration)}
            sx={{ border: '1px solid', borderColor: 'error.main', p: '3px' }}
          >
            {deletingRegistration && registrationToDelete?.id === registration.id ? (
              <CircularProgress size={16} color="error" />
            ) : (
              <DeleteIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        
        {!registration.hasAttended && (
          <Tooltip title="Als anwesend markieren">
            <IconButton
              size="small"
              color="success"
              disabled={updatingRegistrationId === registration.id}
              onClick={() => handleUpdateRegistrationStatus(registration.id, {
                isConfirmed: true,
                hasAttended: true
              })}
              sx={{ border: '1px solid', borderColor: 'success.main', p: '3px' }}
            >
              {markingAttendedId === registration.id ? (
                <CircularProgress size={16} color="success" />
              ) : (
                <PersonIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  );
    const renderTimeslotCard = (timeslot: TimeslotWithInfo) => {
    const startDate = new Date(timeslot.startTime);
    const endDate = new Date(timeslot.endTime);
    const timeStr = `${format(startDate, 'HH:mm')} - ${format(endDate, 'HH:mm')}`;
    
    return (
      <Card 
        key={timeslot.id} 
        sx={{ 
          mb: 2, 
          borderLeft: 4,
          borderLeftColor: 
            timeslot.spotsPercent < 50 ? 'error.main' :
            timeslot.spotsPercent < 75 ? 'warning.main' : 
            'success.main'
        }}
      >
        <CardContent sx={{ pb: 1 }}>
           {timeslot.description && (
            <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
              {timeslot.description}
            </Typography>
          )}
          <Grid container spacing={1}>
            <Grid item xs={9}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                  {timeStr}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'right' }}>
              <Badge 
                badgeContent={timeslot.registeredVolunteers} 
                color={timeslot.spotsLeft === 0 ? "success" : timeslot.spotsLeft <= 2 ? "warning" : "primary"}
                max={99}
              >
                <PersonIcon />
              </Badge>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                von {timeslot.requiredVolunteers}
              </Typography>
            </Grid>
          </Grid>
          
         
          
          {renderCoverageIndicator(timeslot)}
        </CardContent>
        
        <CardActions>
          <Button 
            size="small" 
            startIcon={<AddIcon />} 
            onClick={() => handleOpenNewRegistrationDialog(timeslot.id)}
          >
            Helfer hinzufügen
          </Button>
          
          {timeslot.registrations.length > 0 && (
            <Button
              size="small"
              startIcon={<EmailIcon />}
              onClick={() => handleOpenEmailDialog(timeslot)}
              color="info"
            >
              E-Mail senden
            </Button>
          )}
          
          <Button
            size="small"
            onClick={() => toggleTimeslotExpansion(timeslot.id)}
            endIcon={timeslot.isExpanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          >
            {timeslot.registrations.length > 0 
              ? `${timeslot.registrations.length} Anmeldungen`
              : 'Keine Anmeldungen'}
          </Button>
        </CardActions>
        
        <Collapse in={timeslot.isExpanded} timeout="auto" unmountOnExit>
          <Divider />
          <List dense sx={{ py: 0 }}>
            {timeslot.registrations.length === 0 ? (
              <ListItem>
                <ListItemText 
                  primary="Keine Anmeldungen für dieses Zeitfenster" 
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
            ) : (
              timeslot.registrations.map(registration => renderRegistrationItem(registration))
            )}
          </List>
        </Collapse>
      </Card>
    );
  };
  
  const renderNewVolunteerForm = () => (
    <Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        inputRef={formRefs.firstname}
        label="Vorname"
        name="firstname"
        required
        error={!!registrationFormErrors.firstname}
        helperText={registrationFormErrors.firstname}
      />
      <TextField
        inputRef={formRefs.lastname}
        label="Nachname"
        name="lastname"
        required
        error={!!registrationFormErrors.lastname}
        helperText={registrationFormErrors.lastname}
      />
      <TextField
        inputRef={formRefs.email}
        label="E-Mail"
        name="email"
        required
        error={!!registrationFormErrors.email}
        helperText={registrationFormErrors.email}
      />
      <TextField
        inputRef={formRefs.phoneNumber}
        label="Telefonnummer"
        name="phoneNumber"
      />      <FormControl fullWidth>
        <InputLabel id="commodity-select-label">Organisation</InputLabel>
        <Select
          labelId="commodity-select-label"
          name="commodity"
          label="Organisation"
          inputRef={formRefs.commodity}
          defaultValue=""
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 48 * 4.5
              }
            }
          }}
        >
          <MenuItem value="">Keine</MenuItem>
          <MenuItem value="Musikverein">Musikverein</MenuItem>
          <MenuItem value="Kameradschaftsbund">Kameradschaftsbund</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  // Group timeslots by day
  const groupTimeslotsByDay = (timeslots: TimeslotWithInfo[]) => {
    const grouped: { [key: string]: TimeslotWithInfo[] } = {};
    
    timeslots.forEach(timeslot => {
      const dateKey = format(new Date(timeslot.startTime), 'yyyy-MM-dd');
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(timeslot);
    });
    
    // Sort days chronologically
    const sortedDays = Object.keys(grouped).sort();
    
    return sortedDays.map(day => ({
      date: day,
      displayDate: format(parseISO(day), 'EEEE, dd. MMMM yyyy', { locale: de }),
      timeslots: grouped[day].sort((a, b) => 
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      )
    }));
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Dienstmanagement
        </Typography>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom={isMobile}>
                Dienst auswählen
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="job-select-label">Dienst</InputLabel>
                <Select
                  labelId="job-select-label"
                  id="job-select"
                  value={selectedJobId}
                  onChange={handleJobChange}
                  label="Dienst"                  disabled={loading}
                >
                  {jobManagerData?.jobs
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((job) => (
                      <MenuItem key={job.id} value={job.id}>
                        {job.name} - {job.location}
                      </MenuItem>
                    )) || []}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              {selectedJob && (
                <Box>
                  <Typography variant="subtitle1">
                    {selectedJob.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PlaceIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {selectedJob.location}
                    </Typography>
                  </Box>
                  {selectedJob.description && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedJob.description.length > 100
                        ? `${selectedJob.description.substring(0, 100)}...`
                        : selectedJob.description}
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>
        
        {loading ? (
          <Box display="flex" justifyContent="center" m={4}>
            <CircularProgress />
          </Box>        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            {registrationSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRegistrationSuccess(null)}>
                {registrationSuccess}
              </Alert>
            )}
            
            {registrationError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRegistrationError(null)}>
                {registrationError}
              </Alert>
            )}
          </>
        )}
        
        {selectedJob ? (
          <>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexWrap: 'wrap', 
              mb: 2 
            }}>
              <Typography variant="h5" sx={{ mb: isMobile ? 1 : 0 }}>
                Zeitfenster
              </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {/* Date filter dropdown */}
                <FormControl 
                  size={isSmall ? "small" : "medium"} 
                  sx={{ minWidth: isSmall ? 120 : 180 }}
                >
                  <InputLabel id="date-select-label">Tag</InputLabel>
                  <Select
                    labelId="date-select-label"
                    id="date-select"
                    value={selectedDate}
                    onChange={handleDateChange}
                    label="Tag"
                    disabled={timeslotLoading}
                  >
                    <MenuItem value="all">Alle Tage</MenuItem>
                    {availableDates.map((date) => (
                      <MenuItem key={date} value={date}>
                        {format(new Date(date), 'dd.MM.yyyy, EEEE')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Button
                  variant={lowCoverageOnly ? "contained" : "outlined"}
                  color="warning"
                  startIcon={<WarningIcon />}
                  onClick={() => setLowCoverageOnly(!lowCoverageOnly)}
                  size={isSmall ? "small" : "medium"}
                >
                  {isSmall ? "Problematische" : "Problematische Zeitfenster"}
                </Button>
                
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate(`/admin/jobs/${selectedJobId}`)}
                  startIcon={<VisibilityIcon />}
                  size={isSmall ? "small" : "medium"}
                >
                  {isSmall ? "Details" : "Dienstdetails"}
                </Button>
              </Box>
            </Box>
            
            {timeslotLoading ? (
              <Box display="flex" justifyContent="center" m={4}>
                <CircularProgress />
              </Box>
            ) : filteredTimeslots.length === 0 ? (
              lowCoverageOnly ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Alle Zeitfenster haben ausreichend Helfer.
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Keine Zeitfenster für diesen Dienst gefunden.
                </Alert>
              )            ) : (
              <Box>
                {groupTimeslotsByDay(filteredTimeslots).map(dayGroup => (
                  <Box key={dayGroup.date} sx={{ mb: 4 }}>
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      sx={{ 
                        mb: 2, 
                        pb: 1, 
                        borderBottom: 2, 
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        fontWeight: 'bold'
                      }}
                    >
                      {dayGroup.displayDate}
                    </Typography>
                    <Grid container spacing={2}>
                      {dayGroup.timeslots.map(timeslot => (
                        <Grid item xs={12} key={timeslot.id}>
                          {renderTimeslotCard(timeslot)}
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
              </Box>
            )}
          </>
        ) : (
          <Alert severity="info">
            Bitte wählen Sie einen Dienst aus dem Dropdown-Menü.
          </Alert>
        )}
      </Box>
      
      {/* Dialog for adding a new volunteer registration */}
      <Dialog
        open={newRegistrationDialogOpen}
        onClose={handleCloseNewRegistrationDialog}
        fullWidth
        maxWidth="sm"
        fullScreen={isSmall}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}>
          Helfer hinzufügen
          {isSmall && (
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleCloseNewRegistrationDialog}
              aria-label="close"
              size="small"
            >
              <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
                <path d="M0 0h24v24H0z" fill="none"/>
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </IconButton>
          )}
        </DialogTitle>
        
        <DialogContent dividers>
          {selectedTimeslotId && (
            <Box component="form" sx={{ mt: isSmall ? 1 : 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Zeitfenster
              </Typography>
              {(() => {
                const timeslot = timeslots.find(ts => ts.id === selectedTimeslotId);
                if (!timeslot) return null;
                
                const startDate = new Date(timeslot.startTime);
                const endDate = new Date(timeslot.endTime);
                
                return (
                  <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {format(startDate, 'dd.MM.yyyy')}
                    </Typography>
                    <Typography variant="body2">
                      {format(startDate, 'HH:mm')} - {format(endDate, 'HH:mm')}
                    </Typography>
                  </Box>
                );
              })()}

              <Tabs 
                value={selectedTab} 
                onChange={(_, value) => setSelectedTab(value)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                <Tab label="Existierenden Helfer auswählen" />
                <Tab label="Neuen Helfer anlegen" />
              </Tabs>

              {selectedTab === 0 ? (
                // Existing volunteer selection
                <Autocomplete
                  options={filteredVolunteers}
                  getOptionLabel={(option: VolunteerForSelect) => option.name}
                  value={filteredVolunteers.find((v) => v.id === selectedVolunteerId) || null}
                  onChange={(
                    _event: React.SyntheticEvent<Element, Event>,
                    newValue: VolunteerForSelect | null
                  ) => {
                    setSelectedVolunteerId(newValue ? newValue.id : '');
                    setRegistrationFormErrors((prev) => ({ ...prev, volunteer: '' }));
                  }}
                  isOptionEqualToValue={(
                    option: VolunteerForSelect,
                    value: VolunteerForSelect
                  ) => option.id === value.id}
                  renderOption={(
                    props: React.HTMLAttributes<HTMLLIElement>,
                    option: VolunteerForSelect
                  ) => (
                    <li {...props} key={option.id} style={{ opacity: option.alreadyRegistered || option.hasConflict ? 0.5 : 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body1">{option.name}</Typography>
                        {option.alreadyRegistered && (
                          <Typography variant="caption" color="warning.main">
                            Bereits angemeldet
                          </Typography>
                        )}
                        {option.hasConflict && !option.alreadyRegistered && (
                          <Typography variant="caption" color="error">
                            Zeitkonflikt mit bestehender Anmeldung
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  getOptionDisabled={(option: VolunteerForSelect) => !!option.alreadyRegistered || !!option.hasConflict}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label="Helfer suchen und auswählen"
                      error={!!registrationFormErrors.volunteer}
                      helperText={registrationFormErrors.volunteer}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  fullWidth
                />
              ) : (
                // New volunteer form
                renderNewVolunteerForm()
              )}

              {/* Comment field for registration */}
              <TextField
                label="Kommentar (optional)"
                multiline
                rows={3}
                value={registrationComment}
                onChange={(e) => setRegistrationComment(e.target.value)}
                placeholder="Zusätzliche Informationen zur Anmeldung..."
                fullWidth
                sx={{ mt: 2 }}
              />

              {registrationFormErrors.submit && (
                <Alert severity="error">{registrationFormErrors.submit}</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          py: 2,
          display: 'flex',
          justifyContent: isSmall ? 'space-between' : 'flex-end',
          width: '100%'
        }}>
          <Button 
            onClick={handleCloseNewRegistrationDialog} 
            disabled={creatingRegistration}
            variant={isSmall ? "outlined" : "text"}
            fullWidth={isSmall}
            sx={{ mr: isSmall ? 1 : 0 }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleCreateRegistration}
            color="primary"
            variant={isSmall ? "contained" : "text"}
            disabled={creatingRegistration || (selectedTab === 0 && !selectedVolunteerId)}
            fullWidth={isSmall}
            startIcon={<AddIcon />}
          >
            {creatingRegistration ? 'Erstelle...' : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog for confirming deletion of a registration */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Anmeldung löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie die Anmeldung von{' '}
            <strong>{registrationToDelete?.volunteerName}</strong> löschen möchten?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deletingRegistration}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleDeleteRegistration} 
            color="error" 
            disabled={deletingRegistration}
          >
            {deletingRegistration ? 'Lösche...' : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>
        {/* Dialog for confirming new volunteer registration */}
      <Dialog
        open={newVolunteerConfirmDialogOpen}
        onClose={() => setNewVolunteerConfirmDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Neue Anmeldung bestätigen
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie die Anmeldung von <strong>{newVolunteerData?.name}</strong> für das Zeitfenster am {selectedTimeslotForConfirmation && (
              <>
                <br />
                {format(new Date(timeslots.find(ts => ts.id === selectedTimeslotForConfirmation)?.startTime || ''), 'dd.MM.yyyy, HH:mm')}
              </>
            )} wirklich bestätigen?
          </DialogContentText>
          
          <TextField
            margin="dense"
            label="Kommentar (optional)"
            multiline
            rows={3}
            value={registrationComment}
            onChange={(e) => setRegistrationComment(e.target.value)}
            placeholder="Zusätzliche Informationen zur Anmeldung..."
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>          <Button onClick={() => {
            setNewVolunteerConfirmDialogOpen(false);
            setRegistrationComment('');
            setRegistrationSuccess(null);
            setRegistrationError(null);
          }}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleConfirmNewVolunteerRegistration} 
            color="primary"
            disabled={creatingRegistration}
          >
            {creatingRegistration ? 'Erstelle...' : 'Bestätigen'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog for sending email with timeslot information */}
      <Dialog
        open={emailDialogOpen}
        onClose={handleCloseEmailDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          E-Mail mit Dienstinformationen senden
        </DialogTitle>
        <DialogContent>
          {selectedTimeslotForEmail && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Zeitfenster
              </Typography>
              <Card sx={{ p: 2, bgcolor: 'action.hover' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {format(new Date(selectedTimeslotForEmail.startTime), 'dd.MM.yyyy')}
                </Typography>
                <Typography variant="body2">
                  {format(new Date(selectedTimeslotForEmail.startTime), 'HH:mm')} - {format(new Date(selectedTimeslotForEmail.endTime), 'HH:mm')}
                </Typography>
              </Card>
            </Box>
          )}

          <TextField
            label="Betreff"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Nachricht"
            value={emailMessage}
            onChange={(e) => setEmailMessage(e.target.value)}
            fullWidth
            multiline
            rows={4}
            sx={{ mb: 2 }}
          />
          
          {selectedTimeslotForEmail && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                E-Mail-Empfänger
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  E-Mails werden automatisch an alle Helfer mit E-Mail-Adresse versendet.
                </Typography>
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Erhalten E-Mail
                    </Typography>
                    {selectedTimeslotForEmail.registrations
                      .filter(reg => {
                        const volunteer = volunteers.find(v => v.id === reg.volunteerId);
                        return volunteer?.email;
                      })
                      .map(reg => {
                        const volunteer = volunteers.find(v => v.id === reg.volunteerId);
                        
                        return (
                          <Typography key={reg.id} variant="body2" sx={{ mb: 0.5 }}>
                            • {reg.volunteerName} ({volunteer?.email})
                          </Typography>
                        );
                      })}
                    {selectedTimeslotForEmail.registrations.filter(reg => {
                      const volunteer = volunteers.find(v => v.id === reg.volunteerId);
                      return volunteer?.email;
                    }).length === 0 && (
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        Keine E-Mail-Adressen verfügbar
                      </Typography>
                    )}
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Manuell benachrichtigen
                    </Typography>
                    {selectedTimeslotForEmail.registrations
                      .filter(reg => {
                        const volunteer = volunteers.find(v => v.id === reg.volunteerId);
                        return !volunteer?.email;
                      })
                      .map(reg => {
                        const volunteer = volunteers.find(v => v.id === reg.volunteerId);
                        const phoneNumber = volunteer?.phoneNumber;
                        
                        return (
                          <Box key={reg.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                              • {reg.volunteerName}
                              {phoneNumber && ` (Tel: ${phoneNumber})`}
                            </Typography>
                            {phoneNumber && (
                              <Tooltip title="WhatsApp öffnen">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => {
                                    const formattedPhone = formatPhoneForWhatsApp(phoneNumber);
                                    const message = encodeURIComponent(`Hallo ${reg.volunteerName}, Nachricht vom Strudengauer Messe Team.`);
                                    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${message}`;
                                    window.open(whatsappUrl, '_blank');
                                  }}
                                  sx={{ 
                                    ml: 1, 
                                    border: '1px solid', 
                                    borderColor: 'success.main', 
                                    p: '2px',
                                    backgroundColor: 'rgba(76, 175, 80, 0.1)'
                                  }}
                                >
                                  <ChatIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        );
                      })}
                    {selectedTimeslotForEmail.registrations.filter(reg => {
                      const volunteer = volunteers.find(v => v.id === reg.volunteerId);
                      return !volunteer?.email;
                    }).length === 0 && (
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        Alle haben E-Mail-Adressen
                      </Typography>
                    )}
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
          
          {emailResult && (
            <Alert severity="success" sx={{ mb: 2 }}>
              E-Mails wurden erfolgreich gesendet an:
              <ul>
                {emailResult.sentEmails.map(email => (
                  <li key={email}>{email}</li>
                ))}
              </ul>
              
              {emailResult.skippedEmails.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" color="error">
                    Einige E-Mails konnten nicht gesendet werden:
                  </Typography>
                  <ul>
                    {emailResult.skippedEmails.map(({ volunteerName, reason }) => (
                      <li key={volunteerName}>
                        {volunteerName} - {reason}
                      </li>
                    ))}
                  </ul>
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog} disabled={sendingEmail}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleSendEmail} 
            color="primary"
            disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
          >
            {sendingEmail ? 'Versende...' : 'Senden'}
          </Button>
        </DialogActions>
      </Dialog>
      
    </Container>
  );
};

export default JobManagerPage;
